import type { ReactNode } from "react";
import { Reveal } from "../Reveal";

/**
 * The two shapes every section on this page is built from.
 *
 * 🔴 THE HEADINGS USED TO BE SPARKLE ✦ HEADING ✦ SPARKLE. That treatment is
 * KidShell vocabulary on an adult surface: this page is read by a school
 * administrator deciding whether to run a pilot, and `design-system.md` §8 is
 * explicit that the adult tier is *"warm rather than corporate"* with warmth
 * coming from *"rounded corners, generous spacing, warm neutrals and a rounded
 * typeface — never from cartoon elements"*. A quiet label, a heading and a lede
 * is the same warmth with the cartoon removed.
 *
 * ⚠️ THE LABEL IS SENTENCE CASE, NOT AN ALL-CAPS MICRO-LABEL. §4 forbids those
 * outright and allows exactly one exception — a single eyebrow above a page
 * title — which a page with six sections would have to break six times. The
 * short accent rule beside it does the work the capitals were doing.
 */

export interface SectionProps {
  id?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A page section.
 *
 * `tabIndex={-1}` is not decoration: `useSectionSpy.goTo` focuses the section
 * after scrolling to it, so a keyboard reader who activates "For schools"
 * continues tabbing from the schools content and not from the header.
 * `scroll-mt-24` keeps the heading clear of the sticky header.
 */
export function Section({ id, children, className = "" }: SectionProps) {
  return (
    <section
      id={id}
      tabIndex={-1}
      className={`mx-auto w-full max-w-[1240px] scroll-mt-24 px-6 outline-none lg:px-10 ${className}`.trim()}
    >
      {children}
    </section>
  );
}

export interface SectionHeadingProps {
  label: string;
  /**
   * 🔴 A NODE, NOT A STRING, BECAUSE THE HEADINGS ON THIS PAGE ARE TWO-TONE.
   *
   * "Learning feels like **playtime**" and "Built for **schools.**" put a brand
   * colour on one fragment, and the fragment is its own `copy.ts` key. Slicing
   * an already-joined string here would colour whatever happened to land in the
   * middle — and it would break outright in any language that puts its emphatic
   * word somewhere else. The caller assembles the fragments it was given; this
   * component only sets the type.
   */
  heading: ReactNode;
  lede?: string;
  /** Colour of the short rule beside the label. Defaults to the brand magenta. */
  accentClass?: string;
  /** Colour of the label text itself. */
  labelClass?: string;
  /** `dark` inverts for ink-backed panels. */
  tone?: "light" | "dark";
  /**
   * A control that sits opposite the heading on one line — the games section's
   * "View all games". It drops under the heading below `sm`, because a pill
   * beside a 44 px heading on a 390 px screen leaves the heading four characters
   * per line.
   */
  aside?: ReactNode;
  className?: string;
}

export function SectionHeading({
  label,
  heading,
  lede,
  accentClass = "bg-primary",
  labelClass,
  tone = "light",
  aside,
  className = "",
}: SectionHeadingProps) {
  const dark = tone === "dark";
  const labelColour = labelClass ?? (dark ? "text-line-strong" : "text-muted");

  const block = (
    <div className={aside ? "max-w-[46rem]" : ""}>
      <p className="flex items-center gap-3 font-dash text-dash-xs font-bold">
        <span
          aria-hidden="true"
          className={`h-0.5 w-7 rounded-pill ${accentClass}`}
        />
        <span className={labelColour}>{label}</span>
      </p>

      <h2
        className={`kq-h2 mt-4 font-dash font-extrabold tracking-tight ${dark ? "text-cream" : "text-ink"}`}
      >
        {heading}
      </h2>

      {lede ? (
        <p
          className={`kq-lede mt-4 font-dash ${dark ? "text-line-strong" : "text-ink-soft"}`}
        >
          {lede}
        </p>
      ) : null}
    </div>
  );

  return (
    <Reveal className={aside ? className : `max-w-[46rem] ${className}`.trim()}>
      {aside ? (
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
          {block}
          <div className="shrink-0">{aside}</div>
        </div>
      ) : (
        block
      )}
    </Reveal>
  );
}
